import { UserRole } from '@lmc/ts-common'
import { PermissionId } from '../../../constants/permissionIds'
import type { ToggleConfig } from './types'
import { CapabilityKey } from './types'

// labelKey / copyKey strings are the UPPER_SNAKE_CASE keys from teamConstants
// (added in Task 3 of the plan). `getKeys(teamTranslations)` returns
// `{ KEY: 'KEY' }`, so the constant value equals its key — passing the string
// straight into t() resolves the registered copy.
//
// `permissionId` is the backend ObjectId (from `constants/permissionIds.ts`).
// The wizard writes these directly into `user.permissions` on POST, so they
// must be the canonical ObjectIds — sending the human-readable code (e.g.
// "MANAGE_SAFEGUARDING") trips Mongoose's CastError when it tries to cast to
// [ObjectId].
const TOGGLE_DEFS: Record<CapabilityKey, Omit<ToggleConfig, 'defaultValue'>> = {
  [CapabilityKey.SafeguardingOversight]: {
    key: CapabilityKey.SafeguardingOversight,
    labelKey: 'CAPABILITY_SAFEGUARDING_OVERSIGHT_LABEL',
    bullets: [
      { variant: 'tick', copyKey: 'CAPABILITY_SAFEGUARDING_OVERSIGHT_BULLET1' }
    ],
    permissionId: PermissionId.MANAGE_SAFEGUARDING
  },
  [CapabilityKey.TeamManagement]: {
    key: CapabilityKey.TeamManagement,
    labelKey: 'CAPABILITY_TEAM_MANAGEMENT_LABEL',
    bullets: [
      { variant: 'tick', copyKey: 'CAPABILITY_TEAM_MANAGEMENT_BULLET1' },
      { variant: 'tick', copyKey: 'CAPABILITY_TEAM_MANAGEMENT_BULLET2' },
      { variant: 'cross', copyKey: 'CAPABILITY_TEAM_MANAGEMENT_BULLET3' }
    ],
    permissionId: PermissionId.VIEW_AND_RESEND_TEAM_MEMBERS
  },
  [CapabilityKey.ServiceUserManagement]: {
    key: CapabilityKey.ServiceUserManagement,
    labelKey: 'CAPABILITY_SERVICE_USER_MANAGEMENT_LABEL',
    bullets: [
      {
        variant: 'tick',
        copyKey: 'CAPABILITY_SERVICE_USER_MANAGEMENT_BULLET1'
      },
      {
        variant: 'tick',
        copyKey: 'CAPABILITY_SERVICE_USER_MANAGEMENT_BULLET_DOCUMENTATION'
      },
      {
        variant: 'tick',
        copyKey: 'CAPABILITY_SERVICE_USER_MANAGEMENT_BULLET_RISK'
      },
      {
        variant: 'cross',
        copyKey: 'CAPABILITY_SERVICE_USER_MANAGEMENT_BULLET2'
      }
    ],
    // The two extra bullets (edit Documentation records, archive risk
    // assessments) are granted by this single permission once CM-3517 folds them
    // into CREATE_AND_EDIT_SERVICE_USER_DATA on the backend. This frontend (copy +
    // the bundle convert-on-edit in buildUpdatePayload) depends on CM-3517 being
    // deployed; the feature is flag-gated, so sequence the backend first.
    permissionId: PermissionId.CREATE_AND_EDIT_SERVICE_USER_DATA
  },
  [CapabilityKey.IncidentManagement]: {
    key: CapabilityKey.IncidentManagement,
    labelKey: 'CAPABILITY_INCIDENT_MANAGEMENT_LABEL',
    bullets: [
      { variant: 'tick', copyKey: 'CAPABILITY_INCIDENT_MANAGEMENT_BULLET1' }
    ],
    permissionId: PermissionId.CREATE_AND_MANAGE_INCIDENTS
  },
  [CapabilityKey.DownloadCentreAccess]: {
    key: CapabilityKey.DownloadCentreAccess,
    labelKey: 'CAPABILITY_DOWNLOAD_CENTRE_LABEL',
    bullets: [
      { variant: 'tick', copyKey: 'CAPABILITY_DOWNLOAD_CENTRE_BULLET1' }
    ],
    permissionId: PermissionId.VIEW_REPORTS_AND_EXPORT_HOME_DATA
  },
  [CapabilityKey.MedicationManagement]: {
    key: CapabilityKey.MedicationManagement,
    labelKey: 'CAPABILITY_MEDICATION_MANAGEMENT_LABEL',
    bullets: [
      { variant: 'tick', copyKey: 'CAPABILITY_MEDICATION_MANAGEMENT_BULLET1' }
    ]
    // no permissionId — writes to user.medicationPermission boolean instead
  }
}

const ROLE_TOGGLES: Partial<
  Record<UserRole, Array<{ key: CapabilityKey; defaultValue: boolean }>>
> = {
  [UserRole.careHomeAdmin]: [
    { key: CapabilityKey.SafeguardingOversight, defaultValue: false },
    { key: CapabilityKey.MedicationManagement, defaultValue: true }
  ],
  [UserRole.management]: [
    { key: CapabilityKey.SafeguardingOversight, defaultValue: false },
    { key: CapabilityKey.MedicationManagement, defaultValue: true }
  ],
  [UserRole.seniorCarer]: [
    { key: CapabilityKey.TeamManagement, defaultValue: false },
    { key: CapabilityKey.ServiceUserManagement, defaultValue: false },
    { key: CapabilityKey.IncidentManagement, defaultValue: false },
    { key: CapabilityKey.DownloadCentreAccess, defaultValue: false },
    { key: CapabilityKey.MedicationManagement, defaultValue: true }
  ],
  [UserRole.carer]: [
    { key: CapabilityKey.DownloadCentreAccess, defaultValue: false },
    { key: CapabilityKey.MedicationManagement, defaultValue: false } // Figma AC node 7965:45675
  ]
}

// Accept `string` (not just `UserRole`) to handle all role values cleanly.
export const roleHasStep3 = (role: string): boolean =>
  (ROLE_TOGGLES[role as UserRole]?.length ?? 0) > 0

export const getCapabilityTogglesForRole = (role: string): ToggleConfig[] =>
  (ROLE_TOGGLES[role as UserRole] ?? []).map(({ key, defaultValue }) => ({
    ...TOGGLE_DEFS[key],
    defaultValue
  }))

export const getDefaultCapabilities = (
  role: string
): Partial<Record<CapabilityKey, boolean>> =>
  Object.fromEntries(
    getCapabilityTogglesForRole(role).map((t) => [t.key, t.defaultValue])
  )

// Reverse lookup the Edit-permissions side panel needs: given a backend
// permission ObjectId, find the CapabilityKey that carries it. Only the
// TOGGLE_DEFS entries with a `permissionId` participate (medicationManagement
// is a boolean field, not a permission, so it never matches).
const PERMISSION_ID_TO_CAPABILITY_KEY: Partial<Record<string, CapabilityKey>> =
  Object.fromEntries(
    (Object.values(TOGGLE_DEFS) as Omit<ToggleConfig, 'defaultValue'>[])
      .filter((def) => Boolean(def.permissionId))
      .map((def) => [def.permissionId as string, def.key])
  )

export const getCapabilityKeyForPermissionId = (
  permissionId: string
): CapabilityKey | undefined => PERMISSION_ID_TO_CAPABILITY_KEY[permissionId]
